import { useRef, useState } from "react";
import { api, type Conference, type SetupTemplatePreview as Preview, type SubmissionPhaseDraft } from "../../api.js";
import { apiErrorMessage } from "../../../api/client.js";
import { Button } from "../../../design/components/Button.js";
import { Alert } from "../../../design/components/Feedback.js";
import { SetupTemplatePreview } from "./SetupTemplatePreview.js";

const templates: Array<{ code: Preview["code"]; name: string; detail: string }> = [
  { code: "ABSTRACT_REVIEW", name: "Abstract review", detail: "Collect abstracts, review them, and record decisions." },
  { code: "ABSTRACT_FINAL_PAPER", name: "Abstract to final paper", detail: "Add a final-materials phase after abstract review." },
  { code: "FULL_PAPER_REVIEW", name: "Full-paper peer review", detail: "Collect complete papers before peer review." },
  { code: "POSTERS_PRESENTATIONS", name: "Posters and presentations", detail: "Review proposals and collect accepted presentation files." },
];

function dateAt(offset: number) { const value = new Date(); value.setUTCDate(value.getUTCDate() + offset); return value.toISOString().slice(0, 10); }
function commandKey() { return globalThis.crypto?.randomUUID?.() ?? `setup-${Date.now()}`; }

export function SetupTemplateChooser({ conference, onCompleted, embedded = false }: { conference: Conference; onCompleted: (phase: SubmissionPhaseDraft) => void; embedded?: boolean }) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [dates, setDates] = useState<Record<string, { opens: string; closes: string }>>({});
  const [anonymity, setAnonymity] = useState("DOUBLE_BLIND");
  const [reviewCount, setReviewCount] = useState(2);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const key = useRef(commandKey());

  const choose = async (code: Preview["code"]) => {
    setBusy(true); setError(""); setNotice("");
    try {
      const result = await api.previewSetupTemplate(conference.id, conference.organization_id, code);
      setPreview(result);
      setDates(Object.fromEntries(result.phases.map((phase, index) => [phase.stable_key, { opens: dateAt(index * 15), closes: dateAt(index * 15 + 14) }])));
      setReviewCount(result.review.default_count);
    } catch (reason) { setError(apiErrorMessage(reason, "The template could not be previewed.")); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    if (!preview) return;
    setBusy(true); setError(""); setNotice("");
    try {
      await api.applySetupTemplate(conference.id, conference.organization_id, {
        template_code: preview.code,
        preview_digest: preview.digest,
        phase_dates: Object.fromEntries(Object.entries(dates).map(([stableKey, value]) => [stableKey, { opens_at: `${value.opens}T09:00:00.000Z`, closes_at: `${value.closes}T17:00:00.000Z` }])),
        anonymity,
        review_count: reviewCount,
      }, key.current);
      const firstPhase = await api.submissionPhase(conference.id, conference.organization_id);
      if (!firstPhase) throw new Error("The first submission phase was not created.");
      setNotice("Draft workflow created. Continuing to Collect.");
      onCompleted(firstPhase);
    } catch (reason) { key.current = commandKey(); setError(apiErrorMessage(reason, "The draft workflow could not be created.")); }
    finally { setBusy(false); }
  };

  const changeWorkflow = () => {
    setPreview(null);
    setDates({});
    setError("");
    setNotice("");
  };

  return <section className={`setup-template-chooser${embedded ? " is-embedded" : ""}`}>
    <header><h2>{preview ? "Review draft workflow" : "Choose a starting workflow"}</h2><p>{preview ? "Confirm the phases, dates, and review settings before creating the draft." : "Templates create editable drafts only. Nothing opens or publishes automatically."}</p></header>
    {!preview ? <div className="setup-template-grid">{templates.map((template) => <button type="button" aria-label={template.name} key={template.code} onClick={() => void choose(template.code)} disabled={busy}><strong>{template.name}</strong><span>{template.detail}</span></button>)}</div> : null}
    {error ? <Alert tone="danger">{error}</Alert> : null}
    {preview ? <><div className="setup-template-change"><Button type="button" variant="secondary" disabled={busy} onClick={changeWorkflow}>Change workflow</Button></div><SetupTemplatePreview preview={preview} /><div className="setup-phase-dates">{preview.phases.map((phase) => <fieldset key={phase.stable_key}><legend>{phase.name}</legend><label>Opens<input aria-label={`${phase.name} opens`} type="date" value={dates[phase.stable_key]?.opens ?? ""} onChange={(event) => setDates((current) => ({ ...current, [phase.stable_key]: { ...current[phase.stable_key], opens: event.target.value } }))} /></label><label>Closes<input aria-label={`${phase.name} closes`} type="date" value={dates[phase.stable_key]?.closes ?? ""} onChange={(event) => setDates((current) => ({ ...current, [phase.stable_key]: { ...current[phase.stable_key], closes: event.target.value } }))} /></label></fieldset>)}</div><details><summary>Advanced review settings</summary><label>Review anonymity<select value={anonymity} onChange={(event) => setAnonymity(event.target.value)}><option value="DOUBLE_BLIND">Double blind</option><option value="SINGLE_BLIND">Single blind</option><option value="OPEN">Open</option></select></label><label>Reviews per submission<input type="number" min="1" max="20" value={reviewCount} onChange={(event) => setReviewCount(Number(event.target.value))} /></label></details><Button busy={busy} onClick={() => void apply()}>Create draft workflow</Button></> : null}
    {notice ? <div role="status" className="setup-template-success">{notice}</div> : null}
  </section>;
}
